import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import courseService from '../services/courseService';

const CourseContext = createContext();

export const useCourse = () => {
    const context = useContext(CourseContext);
    if (!context) {
        throw new Error('useCourse must be used within a CourseProvider');
    }
    return context;
};

export const CourseProvider = ({ children }) => {
    const [currentLevel, setCurrentLevel] = useState('pstar');
    const [modules, setModules] = useState([]);
    const [currentModule, setCurrentModule] = useState(null);
    const [currentCourse, setCurrentCourse] = useState(null);
    const [currentChapter, setCurrentChapter] = useState(null);
    const [currentSession, setCurrentSession] = useState(null);
    const [sessionTimer, setSessionTimer] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Timer effect for active sessions (pauses when isPaused is true)
    useEffect(() => {
        let interval;
        if (currentSession && !isPaused) {
            interval = setInterval(() => {
                setSessionTimer(prev => {
                    const newTime = prev + 1;
                    // Auto-save every 30 seconds
                    if (newTime % 30 === 0 && currentSession.sessionId) {
                        courseService.updateSession(currentSession.sessionId, newTime)
                            .catch(err => console.error('Failed to update session:', err));
                    }
                    return newTime;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [currentSession, isPaused]);

    // Load modules for current level
    const loadModules = async (levelId) => {
        setLoading(true);
        setError(null);
        try {
            const data = await courseService.getModules(levelId);
            setModules(data);
            setCurrentLevel(levelId);
            return data;
        } catch (err) {
            setError(err.message);
            console.error('Failed to load modules:', err);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Start or resume a course session
    const startCourse = async (courseId) => {
        // Pre-flight: verify session is still valid (detects another device login)
        try { await api.checkSession(); } catch { return; }
        setLoading(true);
        setError(null);
        try {
            const session = await courseService.startSession(courseId);
            setCurrentSession(session);
            setSessionTimer(session.duration || 0);

            // Load course details
            const courseDetail = await courseService.getCourseDetail(courseId);
            setCurrentCourse(courseDetail.course);

            return session;
        } catch (err) {
            setError(err.message);
            console.error('Failed to start course:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Load chapter content
    const loadChapter = async (chapterId) => {
        setLoading(true);
        setError(null);
        try {
            const [paragraphResult, quizData] = await Promise.all([
                courseService.getParagraphs(chapterId),
                courseService.getQuiz(chapterId)
            ]);

            setCurrentChapter({
                id: chapterId,
                paragraphs: paragraphResult.paragraphs,
                quiz: quizData
            });

            return { paragraphs, quiz: quizData };
        } catch (err) {
            setError(err.message);
            console.error('Failed to load chapter:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Submit quiz answers
    const submitQuiz = async (chapterId, answers) => {
        if (!currentSession?.sessionId) {
            throw new Error('No active session');
        }

        setLoading(true);
        setError(null);
        try {
            const result = await courseService.submitQuiz(
                currentSession.sessionId,
                chapterId,
                answers
            );
            return result;
        } catch (err) {
            setError(err.message);
            console.error('Failed to submit quiz:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // End current session
    const endSession = async () => {
        if (currentSession?.sessionId) {
            try {
                await courseService.updateSession(currentSession.sessionId, sessionTimer);
            } catch (err) {
                console.error('Failed to update final session time:', err);
            }
        }
        setCurrentSession(null);
        setCurrentCourse(null);
        setCurrentChapter(null);
        setSessionTimer(0);
        setIsPaused(false);
    };

    // Format timer display
    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const value = {
        // State
        currentLevel,
        modules,
        currentModule,
        currentCourse,
        currentChapter,
        currentSession,
        sessionTimer,
        isPaused,
        loading,
        error,

        // Actions
        setCurrentLevel,
        setIsPaused,
        setCurrentModule,
        loadModules,
        startCourse,
        loadChapter,
        submitQuiz,
        endSession,
        formatTime
    };

    return (
        <CourseContext.Provider value={value}>
            {children}
        </CourseContext.Provider>
    );
};

export default CourseContext;
